import {
  useGetEntities,
  getGetEntitiesQueryOptions,
} from "@/requests/gen/react-query/entity";
import {
  EntityTypeEnum,
  Service,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Plus } from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import snakeToCamel from "@/lib/snake-to-camel";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { keepPreviousData } from "@tanstack/react-query";
import { resolveEntityImage } from "@/lib/default-image";
import { Header } from "@/components/universal/header";
import { Badge } from "@/components/ui/badge";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";

const entitySearchParsers = {
  entity_types: parseAsArrayOf(
    parseAsStringEnum(Object.values(EntityTypeEnum)),
  ),
};

const entitySearchAllParsers = {
  ...dataTableSearchParsers,
  ...entitySearchParsers,
};

const loadEntitySearch = createLoader(entitySearchAllParsers);

type EntitySearch = inferParserType<typeof entitySearchAllParsers>;

function entityQueryParams(
  p: EntitySearch,
): Parameters<typeof getGetEntitiesQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    entity_types: p.entity_types || [],
  };
}

export const Route = createFileRoute("/_private/_admin/entity/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetEntitiesQueryOptions(
        entityQueryParams(loadEntitySearch(location.searchStr)),
      ),
    ),
  component: EntityLookup,
});

// Define the columns for the table
interface EntityTableProps {
  link?: LinkProps;
  logo: string;
  name: string;
  email: string;
  entity_type: EntityTypeEnum;
  services: Service[];
}

// Define the columns headers and accessor keys
const columns: ColumnDef<EntityTableProps>[] = [
  {
    header: "",
    accessorKey: "logo",
    cell: ({ row }) => (
      <img
        src={row.original.logo}
        alt="Product"
        className="h-12 w-12 rounded-sm object-contain"
      />
    ),
  },
  { header: "Name", accessorKey: "name" },
  { header: "Email", accessorKey: "email" },
  { header: "Type", accessorKey: "entity_type" },
  {
    header: "Services",
    accessorKey: "services",
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {row.original.services.map((service) => (
          <Badge key={service.service}>{snakeToCamel(service.service)}</Badge>
        ))}
      </div>
    ),
  },
];

// Main component
function EntityLookup() {
  const [queryParams, setQueryParams] = useQueryStates(entitySearchAllParsers);

  const entities = useGetEntities(entityQueryParams(queryParams), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  // Map the data to the table format
  const tableData: EntityTableProps[] | undefined = entities.data?.items.map(
    (entity) => {
      return {
        link: linkOptions({
          to: "/entity/$entityId",
          params: { entityId: String(entity.entity_id) },
        }),
        logo: resolveEntityImage(entity),
        name: entity.name,
        email: entity.email || "",
        entity_type: entity.entity_type,
        services: entity.services,
      };
    },
  );

  return (
    <div>
      <title>Entities - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Entities", link: { to: "/entity/lookup" } }]} />
      <Card>
        <CardHeader>
          <CardTitle>Entities</CardTitle>
          <CardDescription>
            View entities below. You can search, filter, and click to see more
            details.
          </CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/entity/create">
                <Plus />
                Create Entity
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={entities.isFetching} />
            <DropdownFilter
              title="Type"
              options={[
                {
                  items: Object.values(EntityTypeEnum).map((entity_type) => ({
                    label: snakeToCamel(entity_type),
                    value: entity_type,
                  })),
                },
              ]}
              values={queryParams.entity_types || []}
              onChange={(values) => setQueryParams({ entity_types: values })}
            />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={entities.isLoading}
            isError={entities.isError}
            count={entities.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
